// frontend/src/components/Cards/SummaryCardWithAreaChart.js

import React from 'react';
import { Paper, Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TinyAreaChart from '../Charts/TinyAreaChart';
import { useTheme, alpha } from '@mui/material/styles';

const SummaryCardWithAreaChart = ({
    icon: Icon,
    label,
    value,
    color,
    tooltip,
    percentageChange,
    chartData,
    badge, // New prop for the badge
    compact = false,
    sx, // Accept sx prop for custom styling
}) => {
    const theme = useTheme();

    return (
        <Paper
            elevation={compact ? 0 : 3}
            sx={{
                p: compact ? 1.25 : 3,
                borderRadius: 2,
                backgroundColor: compact ? '#fcfcfd' : '#fff',
                border: compact ? '1px solid' : 'none',
                borderColor: compact ? 'divider' : 'transparent',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                ...sx,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: compact ? 30 : { xs: 40, sm: 56 },
                            width: compact ? 30 : { xs: 40, sm: 56 },
                            backgroundColor: compact ? alpha(color, 0.12) : color,
                            color: compact ? color : '#fff',
                            borderRadius: compact ? 1.25 : '50%',
                            mr: compact ? 1.25 : 2,
                        }}
                    >
                        <Icon fontSize={compact ? 'small' : 'large'} />
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                            <Typography
                                variant={compact ? 'body2' : 'h6'}
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.2,
                                }}
                            >
                                {label}
                            </Typography>
                            {badge && (
                                <Chip
                                    label={badge}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        ml: 0.75,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                    }}
                                    aria-label={`${badge} badge`}
                                />
                            )}
                            {tooltip && (
                                <Tooltip title={tooltip} arrow>
                                    <IconButton size="small" sx={{ ml: 0.25, p: compact ? 0.25 : 0.5 }}>
                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Typography
                            variant={compact ? 'subtitle1' : 'h5'}
                            sx={{
                                fontWeight: 800,
                                color: compact ? color : theme.palette.text.primary,
                                transition: 'color 0.3s ease',
                                mt: compact ? 0.25 : 0,
                            }}
                        >
                            {value}
                        </Typography>
                    </Box>
                </Box>

                {/* Percentage Change */}
                {percentageChange !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: percentageChange >= 0 ? theme.palette.success.main : theme.palette.error.main,
                                fontWeight: 700,
                                fontSize: { xs: '0.72rem', sm: '0.78rem' },
                            }}
                        >
                            {percentageChange >= 0 ? '↑' : '↓'} {Math.abs(percentageChange).toFixed(2)}%
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* "Last 7 Days" Label */}
            <Typography variant="caption" color="textSecondary" sx={{ mt: compact ? 0.5 : 1 }}>
                {compact ? '7-day trend' : 'Last 7 days'}
            </Typography>

            {/* TinyAreaChart */}
            <Box sx={{ height: compact ? { xs: '38px', sm: '44px' } : { xs: '80px', sm: '120px' }, mt: compact ? 0.15 : 1, overflow: 'hidden' }}>
                <TinyAreaChart data={chartData} color={color} height={compact ? 50 : undefined} />
            </Box>
        </Paper>
    );

};

export default SummaryCardWithAreaChart;
